/**
 * Audio effects: Shift pops, backfires, downshift blips, and redline limiter
 */

export class AudioEffects {
  private ctx: AudioContext;
  private destination: AudioNode;
  private noiseBuffer: AudioBuffer | null = null;

  constructor(ctx: AudioContext, destination: AudioNode) {
    this.ctx = ctx;
    this.destination = destination;
    this.createNoiseBuffer();
  }

  private createNoiseBuffer(): void {
    // Generate 1.0 second of white noise buffer
    const bufferSize = this.ctx.sampleRate;
    this.noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
  }

  /**
   * Play an aggressive gear shift exhaust pop / ignition cut crackle
   * @param intensity 0.0 to 1.0
   */
  public triggerShiftPop(intensity: number = 0.8): void {
    if (!this.noiseBuffer || this.ctx.state !== 'running') return;

    const now = this.ctx.currentTime;
    const duration = 0.08 + Math.random() * 0.06;

    // Noise source
    const source = this.ctx.createBufferSource();
    source.buffer = this.noiseBuffer;

    // Bandpass filter for exhaust resonance
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(320 + Math.random() * 200, now);
    filter.Q.setValueAtTime(3.5, now);

    // Low-end punch oscillator (the bass "thud" of the upshift)
    const thudOsc = this.ctx.createOscillator();
    thudOsc.type = 'sine';
    thudOsc.frequency.setValueAtTime(80, now);
    thudOsc.frequency.exponentialRampToValueAtTime(30, now + duration);

    const thudGain = this.ctx.createGain();
    thudGain.gain.setValueAtTime(0.4 * intensity, now);
    thudGain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    // Gain envelope for noise pop
    const popGain = this.ctx.createGain();
    popGain.gain.setValueAtTime(0.5 * intensity, now);
    popGain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    // Connect noise path
    source.connect(filter);
    filter.connect(popGain);
    popGain.connect(this.destination);

    // Connect thud path
    thudOsc.connect(thudGain);
    thudGain.connect(this.destination);

    source.start(now);
    source.stop(now + duration);
    thudOsc.start(now);
    thudOsc.stop(now + duration);

    // Optional second crackle for aggressive shifts
    if (intensity > 0.6 && Math.random() > 0.4) {
      const crackleTime = now + 0.04 + Math.random() * 0.05;
      const crackleSource = this.ctx.createBufferSource();
      crackleSource.buffer = this.noiseBuffer;

      const crackleFilter = this.ctx.createBiquadFilter();
      crackleFilter.type = 'highpass';
      crackleFilter.frequency.setValueAtTime(800, crackleTime);

      const crackleGain = this.ctx.createGain();
      crackleGain.gain.setValueAtTime(0.25 * intensity, crackleTime);
      crackleGain.gain.exponentialRampToValueAtTime(0.001, crackleTime + 0.04);

      crackleSource.connect(crackleFilter);
      crackleFilter.connect(crackleGain);
      crackleGain.connect(this.destination);

      crackleSource.start(crackleTime);
      crackleSource.stop(crackleTime + 0.05);
    }
  }

  /**
   * Play a subtle mechanical transmission engagement click
   */
  public triggerShiftEngagement(): void {
    if (this.ctx.state !== 'running') return;
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(240, now);
    osc.frequency.exponentialRampToValueAtTime(60, now + 0.03);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

    osc.connect(gain);
    gain.connect(this.destination);

    osc.start(now);
    osc.stop(now + 0.035);
  }
}
